import React, { useState } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { siteContent } from '../data/mockData';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const content = siteContent.en;

  // Helper function to get current page from location
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return 'home';
    if (path.startsWith('/services')) return 'services';
    if (path.startsWith('/about')) return 'about';
    if (path.startsWith('/insights')) return 'insights';
    if (path.startsWith('/contact')) return 'contact';
    return 'home';
  };

  const currentPage = getCurrentPage();

  const handleNavigation = (page, section = null) => {
    navigate(`/${page}${section ? `#${section}` : ''}`);
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const scrollToContact = () => {
    if (location.pathname !== '/contact') {
      handleNavigation('contact');
      return;
    }
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const toggleDropdown = (menuKey) => {
    setActiveDropdown(activeDropdown === menuKey ? null : menuKey);
  };

  const renderDropdownMenu = (menuKey) => {
    const menuData = content.nav[menuKey];
    if (!menuData.submenu) return null;

    return (
      <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
        {menuData.submenu.map((item) => (
          <button
            key={item.id}
            onClick={() => handleNavigation(menuKey, item.id)}
            className="w-full text-left px-4 py-3 text-gray-700 hover:bg-[#f0f0e8] hover:text-[#011431] transition-colors"
          >
            <div className="font-medium">{item.title}</div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-700 bg-[#011431]">
      <div className="container mx-auto flex h-24 items-center justify-between px-4 lg:px-6">
        {/* Logo - Original size maintained */}
        <div className="flex items-center">
          <button onClick={() => handleNavigation('home')} className="flex items-center">
            <Logo variant="white" className="w-[500px] h-[150px] object-contain" />
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          <button
            onClick={() => handleNavigation('home')}
            className={`text-gray-300 hover:text-white font-medium transition-colors ${
              currentPage === 'home' ? 'text-white font-semibold' : ''
            }`}
          >
            {content.nav.home}
          </button>
          
          {/* Services Dropdown - Click to toggle */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('services')}
              className={`flex items-center text-gray-300 hover:text-white font-medium transition-colors ${
                currentPage === 'services' ? 'text-white font-semibold' : ''
              }`}
            >
              {content.nav.services.title}
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform text-gray-300 ${
                activeDropdown === 'services' ? 'rotate-180' : ''
              }`} />
            </button>
            {activeDropdown === 'services' && renderDropdownMenu('services')}
          </div>

          {/* About Dropdown - Click to toggle */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('about')}
              className={`flex items-center text-gray-300 hover:text-white font-medium transition-colors ${
                currentPage === 'about' ? 'text-white font-semibold' : ''
              }`}
            >
              {content.nav.about.title}
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform text-gray-300 ${
                activeDropdown === 'about' ? 'rotate-180' : ''
              }`} />
            </button>
            {activeDropdown === 'about' && renderDropdownMenu('about')}
          </div>

          {/* Insights Dropdown - Click to toggle */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('insights')}
              className={`flex items-center text-gray-300 hover:text-white font-medium transition-colors ${
                currentPage === 'insights' ? 'text-white font-semibold' : ''
              }`}
            >
              {content.nav.insights.title}
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform text-gray-300 ${
                activeDropdown === 'insights' ? 'rotate-180' : ''
              }`} />
            </button>
            {activeDropdown === 'insights' && renderDropdownMenu('insights')}
          </div>

          <button
            onClick={() => handleNavigation('contact')}
            className={`text-gray-300 hover:text-white font-medium transition-colors ${
              currentPage === 'contact' ? 'text-white font-semibold' : ''
            }`}
          >
            {content.nav.contact}
          </button>
        </nav>

        {/* CTA Button */}
        <div className="hidden md:flex items-center">
          <Button
            onClick={scrollToContact}
            className="bg-[#c41927] hover:bg-[#a01520] text-white font-medium px-6 py-3"
          >
            {content.nav.bookConsultation}
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden p-2 text-gray-300 hover:text-white"
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-700 bg-[#011431]">
          <nav className="flex flex-col space-y-2 px-4 py-4">
            <button
              onClick={() => handleNavigation('home')}
              className="text-left text-gray-300 hover:text-white font-medium py-2"
            >
              {content.nav.home}
            </button>
            <button
              onClick={() => handleNavigation('services')}
              className="text-left text-gray-300 hover:text-white font-medium py-2"
            >
              {content.nav.services.title}
            </button>
            <button
              onClick={() => handleNavigation('about')}
              className="text-left text-gray-300 hover:text-white font-medium py-2"
            >
              {content.nav.about.title}
            </button>
            <button
              onClick={() => handleNavigation('insights')}
              className="text-left text-gray-300 hover:text-white font-medium py-2"
            >
              {content.nav.insights.title}
            </button>
            <button
              onClick={() => handleNavigation('contact')}
              className="text-left text-gray-300 hover:text-white font-medium py-2"
            >
              {content.nav.contact}
            </button>
            <div className="pt-4 border-t border-gray-700">
              <Button
                onClick={scrollToContact}
                className="bg-[#c41927] hover:bg-[#a01520] text-white w-full"
              >
                {content.nav.bookConsultation}
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Overlay to close dropdowns when clicking outside */}
      {activeDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setActiveDropdown(null)}
        ></div>
      )}
    </header>
  );
};

export default Header;